import { Application, Assets, Container } from 'pixi.js';
import { StageEvents } from './StageEvents';
import { Viewport } from 'pixi-viewport';
import ConfigurationManager from './ConfigurationManager';
import RENDERER_CONFIG from '@/configs/rendererConfig';

class ConfigurationRenderer {
	readonly scale = RENDERER_CONFIG.RENDERER_SCALE;
	private static instance: ConfigurationRenderer = new ConfigurationRenderer();
	private readyResolve!: () => void;
	ready = new Promise<void>((res) => (this.readyResolve = res));
	manager: ConfigurationManager;
	stageEvents!: StageEvents;
	app = new Application();
	viewport!: Viewport;
	configuration = new Container({ sortableChildren: true });

	private constructor() {
		this.manager = new ConfigurationManager(this);
		this.initialize();
	}

	static getInstance() {
		return ConfigurationRenderer.instance;
	}

	private async initialize() {
		await this.loadFonts();
		await this.app.init({
			background: 0xffffff,
		});

		this.viewport = new Viewport({
			events: this.app.renderer.events,
			disableOnContextMenu: true,
			stopPropagation: true,
			passiveWheel: false,
		});

		this.stageEvents = new StageEvents(this);

		this.viewport
			.drag({ mouseButtons: 'right' })
			.pinch()
			.wheel({ percent: 0.1 })
			.decelerate({ friction: 0.5 })
			.clampZoom({
				minScale: 0.5,
				maxScale: 5,
			})
			.setZoom(1, true);

		this.viewport.cursor = 'grab';

		this.viewport.on('drag-start', (e) => {
			e.viewport.cursor = 'grabbing';
		});
		this.viewport.on('drag-end', (e) => {
			this.viewport.cursor = 'grab';
		});

		this.viewport.on('zoomed-end', () => {});

		this.viewport.addChild(this.configuration);
		this.app.stage.addChild(this.viewport);

		this.manager.initialize();
		this.readyResolve();
	}

	private async loadFonts() {
		Assets.addBundle('fonts', [
			{
				alias: 'IBMPlexSans',
				src: '/IBMPlexSans.ttf',
				data: { family: 'IBMPlexSans' },
			},
		]);

		await Assets.loadBundle('fonts');
	}

	async setRootElement(root: HTMLDivElement) {
		await this.ready;

		this.app.resizeTo = root;
		root.appendChild(this.app.canvas);

		this.viewport.screenWidth = this.app.renderer.width;
		this.viewport.screenHeight = this.app.renderer.height;

		this.configuration.position.set(
			this.viewport.screenWidth / 2,
			this.viewport.screenHeight / 2
		);
	}

	async generateScreenshot() {
		const canvas = this.app.renderer.extract.canvas({
			target: this.configuration,
			resolution: 4,
			clearColor: 0xffffff,
		});

		return new Promise<Blob>((resolve) => {
			canvas.toBlob!(
				(blob) => {
					resolve(blob!);
				},
				'image/png',
				1
			);
		});
	}

	handleResize() {
		if (!this.viewport) return;

		this.viewport.resize(this.app.renderer.width, this.app.renderer.height);
	}

	reset() {
		this.manager.clear();
		const canvas = this.app.canvas;
		if (canvas.parentElement) canvas.parentElement.removeChild(canvas);
	}

	export(sliceOptions: any): any {}

	loadData(data: any) {}
}

export default ConfigurationRenderer;
